import { useMutation, useQueryClient } from '@tanstack/react-query';

import { requireSuccess } from '../../shared/lib/requireSuccess';
import type { LineAccount } from './queries';
import { informationKeys, lineKeys } from './queries';
import { createLineBindCode, unlinkLine, updateInformation } from './settingsApi';

export interface InformationUpdate {
    company_name: string;
    phone_number: string;
    email: string;
    temp_unit: string;
    language: string;
    entry_page: string;
}

/** Saves the account's display name and preferences. */
export const useUpdateInformation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (newInfo: InformationUpdate) => {
            const data = await updateInformation(newInfo);
            if (data?.status === 'fail') {
                throw new Error(data.message ?? 'updateInformation failed');
            }
            return data;
        },
        // Returned so the caller's onSuccess runs once the refetched account is in the cache.
        onSuccess: () => queryClient.invalidateQueries({ queryKey: informationKeys.all }),
    });
};

/** Mints a single-use code the user sends to the LINE official account. */
export const useCreateLineBindCode = () =>
    useMutation({
        mutationFn: async (): Promise<{ code: string; expires_in?: number }> => {
            const data = requireSuccess(await createLineBindCode());
            if (!data.code) {
                throw new Error('createLineBindCode returned no code');
            }
            return data;
        },
    });

/** Unlinks one LINE account. */
export const useUnlinkLine = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (lineUserId: string) => requireSuccess(await unlinkLine(lineUserId)),
        onSuccess: (_data, lineUserId) => {
            queryClient.setQueryData<LineAccount[]>(lineKeys.status(), (accounts) =>
                accounts?.filter((account) => account.line_user_id !== lineUserId)
            );
            queryClient.invalidateQueries({ queryKey: lineKeys.status() });
        },
    });
};
